import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GameView, LoginRequest, RegisterRequest } from '@champdle/contracts';
import { ApiRequestError, api } from './api.js';

const GAME_KEY = ['game'] as const;
const CHAMPIONS_KEY = ['champions'] as const;
const ME_KEY = ['me'] as const;
const STATS_KEY = ['stats'] as const;
const GAMES_KEY = ['games'] as const;

export const useChampions = () =>
  useQuery({ queryKey: CHAMPIONS_KEY, queryFn: api.champions, staleTime: 5 * 60_000 });

export const useCurrentGame = () => useQuery({ queryKey: GAME_KEY, queryFn: api.currentGame });

/**
 * Game actions. A guess with no game in progress starts one first, so a first-time visitor
 * can type straight into the box: nothing is created until they actually play (D-007).
 */
export function useGameActions() {
  const qc = useQueryClient();
  const setGame = (game: GameView): void => {
    qc.setQueryData(GAME_KEY, game);
    if (game.status !== 'in_progress') {
      void qc.invalidateQueries({ queryKey: STATS_KEY });
      void qc.invalidateQueries({ queryKey: GAMES_KEY });
    }
  };
  const resync = (err: unknown): void => {
    // The server's idea of the game won (another tab, or it ended): trust it over the cache.
    if (err instanceof ApiRequestError && err.code === 'no_active_game') {
      void qc.invalidateQueries({ queryKey: GAME_KEY });
    }
  };

  const guess = useMutation({
    mutationFn: async (championId: string): Promise<GameView> => {
      const current = qc.getQueryData<GameView | null>(GAME_KEY);
      if (!current || current.status !== 'in_progress') await api.startGame();
      return api.guess(championId);
    },
    onSuccess: setGame,
    onError: () => void qc.invalidateQueries({ queryKey: GAME_KEY }),
  });
  const start = useMutation({ mutationFn: api.startGame, onSuccess: setGame });
  const giveUp = useMutation({ mutationFn: api.giveUp, onSuccess: setGame, onError: resync });
  return { guess, start, giveUp };
}

export const useMe = () => useQuery({ queryKey: ME_KEY, queryFn: api.me });

/**
 * Logging in, registering and logging out all change who the cookie belongs to, so the
 * cached game belongs to the previous identity and is refetched rather than trusted.
 */
export function useAccountActions() {
  const qc = useQueryClient();
  const identityChanged = async (user: unknown): Promise<void> => {
    qc.setQueryData(ME_KEY, user);
    await Promise.all(
      [GAME_KEY, STATS_KEY, GAMES_KEY].map((queryKey) => qc.invalidateQueries({ queryKey })),
    );
  };
  const register = useMutation({
    mutationFn: (input: RegisterRequest) => api.register(input),
    onSuccess: identityChanged,
  });
  const login = useMutation({
    mutationFn: (input: LoginRequest) => api.login(input),
    onSuccess: identityChanged,
  });
  const logout = useMutation({
    mutationFn: api.logout,
    onSuccess: () => identityChanged(null),
  });
  const deleteAccount = useMutation({
    mutationFn: (password: string) => api.deleteAccount(password),
    onSuccess: () => identityChanged(null),
  });
  return { register, login, logout, deleteAccount };
}

/** Always refetched on opening the page: a merge or another tab may have changed the numbers. */
export const useStats = () =>
  useQuery({ queryKey: STATS_KEY, queryFn: api.stats, refetchOnMount: 'always' });

export const useGameHistory = () =>
  useInfiniteQuery({
    queryKey: GAMES_KEY,
    queryFn: ({ pageParam }) => api.games(pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextBefore,
    refetchOnMount: 'always',
  });

export const useReplay = (id: string) =>
  useQuery({ queryKey: ['replay', id], queryFn: () => api.replay(id) });
